import classNames from "classnames";
import React, { FC, MouseEventHandler } from "react";
import { useLocalizedText } from "../../hooks/context-provider-hooks/use-localized-text";
import { Button } from "../button";
import { BDS_BREADCRUMBS_BACK_BUTTON_DEFAULT_LABEL, BDS_TEXT_TEMPLATE_KEYS } from "../constants";
import { IconNavArrowLeft, IconNavArrowRight } from "../icons";
import { Link, LinkProps } from "../link";
import { Text } from "../text";
import { CSS_CLASSES } from "./breadcrumbs-css-classes";

export interface BreadcrumbsProps {
  /**
   * Link props to be rendered.
   */
  links: LinkProps[];

  /**
   * Current page name that is last element of list.
   */
  currentPageLabel: string;

  /**
   * Label text for the back button that's displayed on the mobile view.
   *
   * If not passed, then an a `TextTemplate` again the `BDS_BREADCRUMBS_BACK_BUTTON_LABEL` key is used.
   *
   * If that TextTemplate is not accessible then a default label is used.
   */
  backButtonLabel?: string;

  /**
   * Optional action executed when back button is clicked. This button is available only on mobile view.
   */
  onBackButtonClick?: MouseEventHandler<HTMLButtonElement>;

  /**
   * Controls the color of the links and button rendered by this component.
   *
   * @default interactive;
   */
  color?: "interactive" | "primary";

  /**
   * Controls if on mobile display only back button or detail breadcrumbs.
   *
   * @default true;
   */
  showBackButtonOnMobile?: boolean;

  /**
   * Controls the additional classNames passed to the component styling.
   * This can be a string containing multiple classes space separated.
   */
  className?: string;

  /**
   * Controls the additional classNames passed to back button.
   * This can be a string containing multiple classes space separated.
   */
  backButtonClassName?: string;
}

/**
 * The `Breadcrumbs` component enables users to trigger navigation actions on the user interface.
 *
 * The `Breadcrumbs` component accomplishes navigating back to previous pages and Reminding one where they are, and how they got there.
 */
export const Breadcrumbs: FC<BreadcrumbsProps> = ({
  links,
  currentPageLabel,
  backButtonLabel,
  onBackButtonClick,
  color = "interactive",
  showBackButtonOnMobile = true,
  className,
  backButtonClassName,
  ...restProps
}) => {
  const localizedText = useLocalizedText(true);
  const rootClassName = classNames({ [CSS_CLASSES.BREADCRUMBS_ROOT]: showBackButtonOnMobile }, className);
  const rootMobileClassName = classNames(CSS_CLASSES.BREADCRUMBS_BACK_BUTTON, backButtonClassName);

  return (
    <>
      {links.length > 0 && currentPageLabel && (
        <nav aria-label="Breadcrumbs" className={rootClassName} {...restProps}>
          <ol className={CSS_CLASSES.BREADCRUMBS_LIST}>
            {links.map(({ className, ...linkProps }, i) => (
              <li key={`${linkProps.href}-${i}`}>
                <Link
                  color={color}
                  underline="always"
                  {...linkProps}
                  className={classNames(CSS_CLASSES.BREADCRUMBS_LIST_ITEM, className)}
                />
                <IconNavArrowRight aria-hidden={true} className={CSS_CLASSES.BREADCRUMBS_ITEM_SEPARATOR} />
              </li>
            ))}
            <li>
              <Text aria-current="page" className={CSS_CLASSES.BREADCRUMBS_LAST_ITEM}>
                {currentPageLabel}
              </Text>
            </li>
          </ol>
        </nav>
      )}
      {showBackButtonOnMobile && links.length > 0 && (
        <Button
          startIcon={<IconNavArrowLeft />}
          onClick={onBackButtonClick}
          href={onBackButtonClick ? undefined : links[links.length - 1].href}
          label={
            backButtonLabel ||
            (localizedText
              ? localizedText(BDS_TEXT_TEMPLATE_KEYS.BDS_BREADCRUMBS_BACK_BUTTON_LABEL)
              : BDS_BREADCRUMBS_BACK_BUTTON_DEFAULT_LABEL)
          }
          variant="ghost"
          color={color}
          size="medium"
          width="full"
          labelAlign="left"
          className={rootMobileClassName}
          {...restProps}
        />
      )}
    </>
  );
};
